import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Switch,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { instance } from '@/config/axios';
import { urls } from '@/config/urls';
import { EnableStopUnitOption, enableStopUnitOptions } from '@/actions/types';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { i18next } from '@/config/i18n';
import { makeStyles } from '@mui/styles';
import { useToast } from '../Dashboard/ToastContext';
import { useSettings } from './SettingsContext';

const useStyles = makeStyles(() => ({
  Mainbox: {
    height: '91%',
    width: '100%',
  },
}));

// Discriminated union mirroring the backend `shipment_scheduling` schema.
// `load_thresholds` is only valid on the `load_based_pickups` variant — the
// other policies forbid it, so it isn't even reachable without narrowing.
interface DeliveriesFirstScheduling {
  policy: 'deliveries_first';
  load_thresholds?: null;
}
interface ShortestPathScheduling {
  policy: 'shortest_path';
  load_thresholds?: null;
}
interface LoadBasedPickupScheduling {
  policy: 'load_based_pickups';
  load_thresholds: (number | null)[];
}
type ShipmentScheduling =
  | DeliveriesFirstScheduling
  | ShortestPathScheduling
  | LoadBasedPickupScheduling;

type SchedulingPolicy = ShipmentScheduling['policy'];

const SCHEDULING_POLICIES: SchedulingPolicy[] = [
  'deliveries_first',
  'shortest_path',
  'load_based_pickups',
];

interface OptimizationSettings {
  shipment_scheduling: ShipmentScheduling;
  allow_multiple_routes: boolean;
  avoid_ferries: boolean;
  avoid_countries: string[];
  enable_stop_units: EnableStopUnitOption;
  stop_units_distance_m: number;
  is_initial_loading_working_time: boolean;
  is_final_unloading_working_time: boolean;
  stop_time_must_fit_in_time_window: boolean;
  use_territories: boolean;
}

const switchSettings: (keyof Pick<OptimizationSettings,
  'allow_multiple_routes' |
  'avoid_ferries' |
  'is_initial_loading_working_time' |
  'is_final_unloading_working_time' |
  'stop_time_must_fit_in_time_window' |
  'use_territories'
>)[] = [
    'allow_multiple_routes',
    'avoid_ferries',
    'is_initial_loading_working_time',
    'is_final_unloading_working_time',
    'stop_time_must_fit_in_time_window',
    'use_territories',
  ];

const InfoIcon = ({ message }: { message: string }) => (
  <Tooltip title={message} arrow placement="top">
    <IconButton size="small" sx={{ p: 0.5 }}>
      <InfoOutlined fontSize="small" />
    </IconButton>
  </Tooltip>
);

const SwitchField: React.FC<{
  settingKey: keyof OptimizationSettings;
  value: boolean;
  onChange: (key: keyof OptimizationSettings, value: boolean) => void;
}> = ({ settingKey, value, onChange }) => (
  <Box display="flex" alignItems="center" sx={{ mb: 1, gap: 2 }}>
    <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
      <Typography>{i18next.t(`common:optimizationSetting.${settingKey}.label`)}</Typography>
      <InfoIcon message={i18next.t(`common:optimizationSetting.${settingKey}.info`)} />
    </Box>
    <Switch checked={value} onChange={(e) => onChange(settingKey, e.target.checked)} />
  </Box>
);

const OptimizationSetting: React.FC = () => {
  const [settings, setSettings] = useState<OptimizationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countryInput, setCountryInput] = useState<string>('');
  const { server } = urls;
  const classes = useStyles();
  const { success, error: toastError } = useToast();
  const { settings: appSettings } = useSettings();
  const dimensions = appSettings?.load_dimensions ?? [];
  // Remembers the most recent load thresholds (seeded from the backend on load,
  // kept current as the user edits) so switching policy away and back doesn't
  // lose them until the next save.
  const lastThresholdsRef = useRef<(number | null)[] | null>(null);

  useEffect(() => {
    instance
      .get(server.settings.optimization)
      .then((res) => {
        setSettings(res.data);
        setCountryInput(res.data.avoid_countries?.join(', ') || '');
        setLoading(false);
      })
      .catch(() => {
        setError(i18next.t('common:errors.failedToLoadSettings'));
        setLoading(false);
      });
  }, []);

  const handleChange = useCallback(
    (key: keyof OptimizationSettings, value: boolean | number | string | string[]) => {
      setSettings(prev => prev ? { ...prev, [key]: value } : prev);
    },
    []
  );

  // Keep the remembered thresholds in sync while `load_based_pickups` is active.
  useEffect(() => {
    if (settings?.shipment_scheduling.policy === 'load_based_pickups') {
      lastThresholdsRef.current = settings.shipment_scheduling.load_thresholds;
    }
  }, [settings]);

  // Switching to `load_based_pickups` restores the last-known thresholds (backend
  // value or in-progress edits), sized to the current dimensions. The other
  // variants forbid thresholds, so they get a clean object.
  const handlePolicyChange = useCallback(
    (policy: SchedulingPolicy) => {
      setSettings(prev => {
        if (!prev) return prev;
        if (policy !== 'load_based_pickups') {
          return { ...prev, shipment_scheduling: { policy } };
        }
        const load_thresholds = dimensions.map(
          (_, i): number | null => lastThresholdsRef.current?.[i] ?? null
        );
        return { ...prev, shipment_scheduling: { policy, load_thresholds } };
      });
    },
    [dimensions]
  );

  const handleThresholdChange = useCallback(
    (index: number, value: number | null) => {
      setSettings(prev => {
        if (!prev || prev.shipment_scheduling.policy !== 'load_based_pickups') return prev;
        const load_thresholds = prev.shipment_scheduling.load_thresholds.slice();
        load_thresholds[index] = value;
        return { ...prev, shipment_scheduling: { policy: 'load_based_pickups', load_thresholds } };
      });
    },
    []
  );

  const handleSave = () => {
    if (!settings) return;

    const countryList = countryInput
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter((c) => /^\w{3}$/.test(c));

    const payload = {
      ...settings,
      avoid_countries: countryList,
    };

    setSaving(true);
    instance
      .patch(server.settings.optimization, payload)
      .then(() => {
        success(
          <>
            <b>{i18next.t('common:toast.successTitle')}:</b>&nbsp;
            {i18next.t('common:toast.saveSuccess')}
          </>
        );
      })
      .catch(() => {
        toastError(
          <>
            <b>{i18next.t('common:toast.errorTitle')}:</b>&nbsp;
            {i18next.t('common:toast.saveError')}
          </>
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box className={classes.Mainbox} sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
      <div style={{ width: '70%' }}>
        <TableContainer component={Box}
          sx={{
            mt: 2,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            backgroundColor: 'white',
          }}>
          <Table>
            <TableBody>
              {/* Shipment scheduling policy */}
              <TableRow hover>
                <TableCell align="left" sx={{ py: 0.5, px: 1, pl: 1.75 }}>
                  <Box display="flex" alignItems="center">
                    <Typography>
                      {i18next.t('common:optimizationSetting.shipment_scheduling.label')}
                    </Typography>
                    <InfoIcon
                      message={i18next.t('common:optimizationSetting.shipment_scheduling.info')}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    fullWidth
                    value={settings.shipment_scheduling.policy}
                    onChange={(e) => handlePolicyChange(e.target.value as SchedulingPolicy)}
                  >
                    {SCHEDULING_POLICIES.map((policy) => (
                      <MenuItem key={policy} value={policy}>
                        {i18next.t(`common:optimizationSetting.shipment_scheduling.policies.${policy}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>

              {/* Selective disclosure: load thresholds only for the load_based_pickups policy */}
              {settings.shipment_scheduling.policy === 'load_based_pickups' &&
                (dimensions.length > 0 ? (
                  dimensions.map((dim, index) => (
                    <TableRow hover key={dim.dimension}>
                      <TableCell align="left" sx={{ py: 0.5, px: 1, pl: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {i18next.t('common:optimizationSetting.shipment_scheduling.loadThreshold', {
                            dimension: dim.dimension,
                          })}
                          {dim.unit ? ` (${dim.unit})` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          fullWidth
                          value={
                            settings.shipment_scheduling.policy === 'load_based_pickups'
                              ? settings.shipment_scheduling.load_thresholds[index] ?? ''
                              : ''
                          }
                          onChange={(e) =>
                            handleThresholdChange(
                              index,
                              e.target.value === '' ? null : Number(e.target.value)
                            )
                          }
                          inputProps={{ min: 0 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ py: 0.5, px: 1, pl: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {i18next.t('common:optimizationSetting.shipment_scheduling.noDimensions')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

              {switchSettings.map((key) => (
                <TableRow key={key} hover>
                  <TableCell align="left" sx={{ py: 0.5, px: 1, pl: 1.75 }}>
                    <Box display="flex" alignItems="center">
                      <Typography>{i18next.t(`common:optimizationSetting.${key}.label`)}</Typography>
                      <InfoIcon message={i18next.t(`common:optimizationSetting.${key}.info`)} />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                    <Switch
                      checked={settings?.[key] ?? false}
                      onChange={(e) => handleChange(key, e.target.checked)}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {/* Enable Stop Units */}
              <TableRow hover>
                <TableCell sx={{ py: 0.5, px: 1, pl: 1.75 }}>
                  <Typography>
                    {i18next.t('common:optimizationSetting.enable_stop_units.label')}
                    <InfoIcon message={i18next.t('common:optimizationSetting.enable_stop_units.info')} />
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    fullWidth
                    value={settings.enable_stop_units}
                    onChange={(e) =>
                      handleChange('enable_stop_units', e.target.value as EnableStopUnitOption)
                    }
                  >
                    {enableStopUnitOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>

              {/* Stop Units Distance */}
              <TableRow hover>
                <TableCell sx={{ py: 0.5, px: 1, pl: 1.75 }}>
                  <Typography>
                    {i18next.t('common:optimizationSetting.stop_units_distance_m.label')}
                    <InfoIcon
                      message={i18next.t('common:optimizationSetting.stop_units_distance_m.info')}
                    />
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    fullWidth
                    value={settings.stop_units_distance_m}
                    onChange={(e) => handleChange('stop_units_distance_m', parseInt(e.target.value))}
                    inputProps={{ min: 0 }}
                  />
                </TableCell>
              </TableRow>

              {/* Avoid Countries */}
              <TableRow hover>
                <TableCell sx={{ py: 0.5, px: 1, pl: 1.75 }}>
                  <Typography>
                    {i18next.t('common:optimizationSetting.avoid_countries.label')}
                    <InfoIcon message={i18next.t('common:optimizationSetting.avoid_countries.info')} />
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    placeholder="Länder Vermeiden (3-Buchstaben-ISO-Codes, durch Komma getrennt)"
                    value={countryInput}
                    onChange={(e) => setCountryInput(e.target.value)}
                    helperText={i18next.t('common:optimizationSetting.avoid_countries.helper')}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '15px 0px' }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving
              ? i18next.t('common:settings.saving')
              : i18next.t('common:settings.save')}
          </Button>
        </div>
      </div>
    </Box>
  );
};

export default OptimizationSetting;
